import { useState, useEffect, useCallback } from "react";
import { apiGet } from "../api/client";
import { PageHeader } from "../components/ui/Primitives";
import { fmtKstShort } from "../lib/format";
import {
  Download,
  Database,
  FileArchive,
  Terminal,
  CheckCircle2,
  Copy,
  Layers,
  BarChart3,
  Flame,
  RefreshCw,
  Calendar,
} from "lucide-react";

interface ExportSummary {
  ohlcv?: { count: number; min_ts: string | null; max_ts: string | null; symbols: string[] };
  funding?: { count: number; min_ts: string | null; max_ts: string | null };
  trades?: { count: number; min_ts: string | null; max_ts: string | null; strategies: string[] };
  supply_demand?: { count: number; min_ts: string | null; max_ts: string | null };
  news?: { count: number; min_ts: string | null; max_ts: string | null };
  saveticker?: {
    available?: boolean;
    features?: { count: number; min_ts: string | null; max_ts: string | null };
    filings?: { count: number; min_ts: string | null; max_ts: string | null };
    insiders?: { count: number; min_ts: string | null; max_ts: string | null };
    events?: { count: number; min_ts: string | null; max_ts: string | null };
  };
  supported_symbols?: string[];
}

const SAVETICKER_TABLES = new Set([
  "saveticker_features",
  "saveticker_filings",
  "saveticker_insiders",
  "saveticker_events",
]);

export default function DataExport() {
  const [summary, setSummary] = useState<ExportSummary | null>(null);
  const [loading, setLoading] = useState(false);
  const [selectedTable, setSelectedTable] = useState<string>("ohlcv");
  const [selectedSymbol, setSelectedSymbol] = useState<string>("");
  const [selectedStrategy, setSelectedStrategy] = useState<string>("");
  const [selectedDays, setSelectedDays] = useState<number>(7);
  const [dateMode, setDateMode] = useState<"preset" | "custom">("preset");
  const [customStartDate, setCustomStartDate] = useState<string>(() => new Date().toISOString().slice(0, 10));
  const [customEndDate, setCustomEndDate] = useState<string>(() => new Date().toISOString().slice(0, 10));

  // Bundle custom date state
  const [bundleMode, setBundleMode] = useState<"preset" | "custom">("preset");
  const [bundleStartDate, setBundleStartDate] = useState<string>(() => new Date().toISOString().slice(0, 10));
  const [bundleEndDate, setBundleEndDate] = useState<string>(() => new Date().toISOString().slice(0, 10));

  const [selectedFormat, setSelectedFormat] = useState<string>("csv");
  const [copiedCli, setCopiedCli] = useState<string | null>(null);

  const fetchSummary = useCallback(async () => {
    setLoading(true);
    try {
      const data = await apiGet<ExportSummary>("/export/summary");
      setSummary(data);
    } catch {
      // ignore
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchSummary();
  }, [fetchSummary]);

  const handleCopy = (text: string, label: string) => {
    navigator.clipboard.writeText(text);
    setCopiedCli(label);
    setTimeout(() => setCopiedCli(null), 2500);
  };

  const getDownloadUrl = () => {
    const params = new URLSearchParams({
      table: selectedTable,
      format: selectedFormat,
    });
    if (dateMode === "custom" && customStartDate) {
      params.set("start_date", customStartDate);
      if (customEndDate) params.set("end_date", customEndDate);
    } else {
      params.set("days", String(selectedDays));
    }
    if (selectedSymbol && !SAVETICKER_TABLES.has(selectedTable)) {
      params.set("symbol", selectedSymbol);
    }
    if (selectedTable === "trades" && selectedStrategy) params.set("strategy", selectedStrategy);
    return `/api/export/download?${params.toString()}`;
  };

  const getBundleUrl = (daysOrRange: number | { startDate: string; endDate?: string }) => {
    const params = new URLSearchParams();
    if (typeof daysOrRange === "object") {
      params.set("start_date", daysOrRange.startDate);
      if (daysOrRange.endDate) params.set("end_date", daysOrRange.endDate);
    } else {
      params.set("days", String(daysOrRange));
    }
    return `/api/export/bundle-v3?${params.toString()}`;
  };

  const formatNumber = (num?: number) => {
    if (num === undefined || num === null) return "0";
    return num.toLocaleString();
  };

  const formatTs = (ts?: string | null) => fmtKstShort(ts);

  const dockerCliSnippet = dateMode === "custom" && customStartDate
    ? `docker exec -i koru-timescaledb psql -U koru -d koru -c \\
"COPY (SELECT * FROM md_ohlcv WHERE ts >= '${customStartDate} 00:00:00+00' AND ts <= '${customEndDate || customStartDate} 23:59:59+00' AND symbol IN ('BTCUSDT','ETHUSDT','SOLUSDT','KORUUSDT','SKHYUSDT')) TO STDOUT WITH CSV HEADER" | gzip > md_ohlcv_1m.csv.gz`
    : `docker exec -i koru-timescaledb psql -U koru -d koru -c \\
"COPY (SELECT * FROM md_ohlcv WHERE ts >= NOW() - INTERVAL '${selectedDays} days' AND symbol IN ('BTCUSDT','ETHUSDT','SOLUSDT','KORUUSDT','SKHYUSDT')) TO STDOUT WITH CSV HEADER" | gzip > md_ohlcv_1m.csv.gz`;

  const backtestCliSnippet = `python -m tools.backtest \\
  --csv md_ohlcv_1m.csv \\
  --funding-csv md_funding.csv \\
  --symbols BTCUSDT,ETHUSDT,SOLUSDT,KORUUSDT,SKHYUSDT \\
  --strategies I \\
  --out backtest_result.csv`;

  return (
    <div className="page data-export-page">
      <PageHeader
        icon={<Download size={24} color="var(--accent)" />}
        title="데이터 추출 및 연구소"
        description="TimescaleDB 실시간 마켓 데이터·SaveTicker 피처·체결 이력을 원클릭으로 추출해 오프라인 백테스트와 퀀트 연구에 활용합니다."
        actions={
          <button className="btn-secondary" onClick={fetchSummary} disabled={loading}>
            <RefreshCw size={14} className={loading ? "spin" : ""} />
            새로고침
          </button>
        }
      />

      {/* 1. Database Freshness & Volume Metrics */}
      <div className="grid grid-4" style={{ marginBottom: "var(--space-4)" }}>
        <div className="metric-tile">
          <div className="metric-tile-head">
            <span className="metric-tile-label">1분봉 OHLCV 시계열</span>
            <Database size={15} color="var(--accent)" />
          </div>
          <div className="metric-tile-value">
            {formatNumber(summary?.ohlcv?.count)}{" "}
            <span style={{ fontSize: "var(--text-xs)", color: "var(--text-muted)" }}>건</span>
          </div>
          <div className="metric-tile-sub">최신 데이터: {formatTs(summary?.ohlcv?.max_ts)}</div>
        </div>

        <div className="metric-tile">
          <div className="metric-tile-head">
            <span className="metric-tile-label">펀딩비 및 미결제약정</span>
            <Flame size={15} color="var(--orange)" />
          </div>
          <div className="metric-tile-value">
            {formatNumber(summary?.funding?.count)}{" "}
            <span style={{ fontSize: "var(--text-xs)", color: "var(--text-muted)" }}>건</span>
          </div>
          <div className="metric-tile-sub">최신 데이터: {formatTs(summary?.funding?.max_ts)}</div>
        </div>

        <div className="metric-tile">
          <div className="metric-tile-head">
            <span className="metric-tile-label">페이퍼 소크 체결 이력</span>
            <Layers size={15} color="var(--green)" />
          </div>
          <div className="metric-tile-value">
            {formatNumber(summary?.trades?.count)}{" "}
            <span style={{ fontSize: "var(--text-xs)", color: "var(--text-muted)" }}>건</span>
          </div>
          <div className="metric-tile-sub">전략: {(summary?.trades?.strategies || []).join(", ") || "F, G, I"}</div>
        </div>

        <div className="metric-tile">
          <div className="metric-tile-head">
            <span className="metric-tile-label">SaveTicker 피처</span>
            <Database size={15} color="var(--purple, #8b5cf6)" />
          </div>
          <div className="metric-tile-value">
            {formatNumber(summary?.saveticker?.features?.count)}{" "}
            <span style={{ fontSize: "var(--text-xs)", color: "var(--text-muted)" }}>건</span>
          </div>
          <div className="metric-tile-sub">
            {summary?.saveticker?.available === false
              ? "migration 미적용"
              : `공시 ${formatNumber(summary?.saveticker?.filings?.count)} · 내부자 ${formatNumber(
                  summary?.saveticker?.insiders?.count,
                )} · 최신 ${formatTs(summary?.saveticker?.features?.max_ts)}`}
          </div>
        </div>
      </div>

      {/* 2. One-Click Research Pack Bundle */}
      <div className="card" style={{ marginBottom: "var(--space-4)" }}>
        <div className="card-title-row">
          <div className="card-title">
            <FileArchive size={18} color="var(--accent)" />
            <span>원클릭 연구용 데이터 팩 (.ZIP)</span>
          </div>
          <span className="badge badge-blue">원클릭 통합 번들</span>
        </div>
        <p style={{ fontSize: "var(--text-sm)", color: "var(--text-secondary)", marginBottom: "var(--space-4)" }}>
          OHLCV(1분봉), 펀딩 관측값, 페이퍼 체결, 수급, 뉴스, 계좌 스냅샷과 호가 이력에 뉴스 점수 수정 이력과 판단 당시 조회한 입력을 함께 담습니다. 모든 파일을 같은 시점에 추출하며, 무결성 검증 정보와 사용 가이드를 제공합니다. 입력 이력은 수집 기능 적용 이후부터 포함됩니다.
        </p>

        <div className="btn-row" style={{ alignItems: "center" }}>
          <a
            href={getBundleUrl(1)}
            download
            className="btn-secondary"
            title="오늘 24시간 전체 마켓 데이터 압축팩"
          >
            <Download size={15} />
            <span>오늘 하루 팩 (1일 / 24h)</span>
          </a>
          <a
            href={getBundleUrl(3)}
            download
            className="btn-secondary"
          >
            <Download size={15} />
            <span>최근 3일 팩</span>
          </a>
          <a
            href={getBundleUrl(7)}
            download
            className="btn-primary"
          >
            <Download size={15} />
            <span>최근 7일 연구 팩 (추천)</span>
          </a>
          <a
            href={getBundleUrl(14)}
            download
            className="btn-secondary"
          >
            <Download size={15} />
            <span>최근 14일 팩</span>
          </a>
          <a
            href={getBundleUrl(30)}
            download
            className="btn-secondary"
          >
            <Download size={15} />
            <span>최근 30일 전체 팩</span>
          </a>
          <button
            className={`btn-secondary ${bundleMode === "custom" ? "btn-active" : ""}`}
            onClick={() => setBundleMode(bundleMode === "custom" ? "preset" : "custom")}
            style={{ display: "inline-flex", alignItems: "center", gap: 6 }}
          >
            <Calendar size={15} />
            <span>{bundleMode === "custom" ? "지정 닫기" : "날짜 직접 지정..."}</span>
          </button>
        </div>

        {bundleMode === "custom" && (
          <div
            style={{
              marginTop: "var(--space-4)",
              padding: "var(--space-3)",
              background: "var(--bg-secondary)",
              borderRadius: "var(--radius-md)",
              border: "1px solid var(--border)",
            }}
          >
            <div style={{ display: "flex", flexWrap: "wrap", alignItems: "flex-end", gap: "var(--space-3)" }}>
              <div>
                <label style={{ display: "block", fontSize: "var(--text-xs)", marginBottom: 4 }}>
                  시작일 (Start Date)
                </label>
                <input
                  type="date"
                  value={bundleStartDate}
                  onChange={(e) => setBundleStartDate(e.target.value)}
                  style={{ padding: "6px 10px" }}
                />
              </div>
              <div>
                <label style={{ display: "block", fontSize: "var(--text-xs)", marginBottom: 4 }}>
                  종료일 (End Date)
                </label>
                <input
                  type="date"
                  value={bundleEndDate}
                  onChange={(e) => setBundleEndDate(e.target.value)}
                  style={{ padding: "6px 10px" }}
                />
              </div>
              <a
                href={getBundleUrl({ startDate: bundleStartDate, endDate: bundleEndDate })}
                download
                className="btn-primary"
                style={{ height: 36, display: "inline-flex", alignItems: "center" }}
              >
                <Download size={15} />
                <span>선택 기간 팩 다운로드 (.ZIP)</span>
              </a>
            </div>
            <div className="muted-note" style={{ marginTop: 6 }}>
              * 시작일과 종료일을 동일하게 설정하면(예: 오늘 2026-09-07) 해당 일자의 00:00~23:59(UTC) 데이터만 정확히 압축됩니다.
            </div>
          </div>
        )}
      </div>

      {/* 3. Custom Single Dataset Exporter */}
      <div className="card" style={{ marginBottom: "var(--space-4)" }}>
        <div className="card-title-row">
          <div className="card-title">
            <BarChart3 size={18} color="var(--green)" />
            <span>맞춤형 개별 데이터셋 추출</span>
          </div>
        </div>

        <div className="grid grid-4" style={{ marginTop: "var(--space-3)", gap: "var(--space-3)" }}>
          <div>
            <label style={{ display: "block", marginBottom: 6 }}>대상 데이터셋</label>
            <select
              value={selectedTable}
              onChange={(e) => setSelectedTable(e.target.value)}
              style={{ width: "100%" }}
            >
              <option value="ohlcv">1분봉 OHLCV (md_ohlcv)</option>
              <option value="funding">실시간 펀딩비 (md_funding)</option>
              <option value="trades">페이퍼 매매체결 (paper_trade)</option>
              <option value="supply_demand">외인·기관 수급 (md_supply_demand)</option>
              <option value="news">뉴스 & 매크로 이벤트 (news_event)</option>
              <option value="saveticker_features">SaveTicker 피처 (feature_observation)</option>
              <option value="saveticker_filings">SaveTicker 공시 (filing_revision)</option>
              <option value="saveticker_insiders">SaveTicker 내부자 거래 (insider_observation)</option>
              <option value="saveticker_events">SaveTicker 이벤트 (ticker_event)</option>
            </select>
          </div>

          <div>
            <label style={{ display: "block", marginBottom: 6 }}>종목 필터</label>
            <select
              value={selectedSymbol}
              onChange={(e) => setSelectedSymbol(e.target.value)}
              style={{ width: "100%" }}
              disabled={SAVETICKER_TABLES.has(selectedTable)}
            >
              <option value="">
                {SAVETICKER_TABLES.has(selectedTable)
                  ? "SaveTicker 22종목 전체"
                  : "전체 핵심 종목 (6종)"}
              </option>
              {(summary?.supported_symbols || ["BTCUSDT", "ETHUSDT", "SOLUSDT", "KORUUSDT", "SKHYUSDT", "SOXLUSDT"]).map((sym) => (
                <option key={sym} value={sym} disabled={SAVETICKER_TABLES.has(selectedTable)}>
                  {sym}
                </option>
              ))}
            </select>
          </div>

          <div>
            <label style={{ display: "block", marginBottom: 6 }}>기간</label>
            <select
              value={dateMode === "custom" ? "custom" : String(selectedDays)}
              onChange={(e) => {
                if (e.target.value === "custom") {
                  setDateMode("custom");
                } else {
                  setDateMode("preset");
                  setSelectedDays(Number(e.target.value));
                }
              }}
              style={{ width: "100%" }}
            >
              <option value="1">최근 24시간 (오늘 당일, 1일)</option>
              <option value="3">최근 3일 (주말 포함)</option>
              <option value="7">최근 7일 (추천)</option>
              <option value="14">최근 14일</option>
              <option value="30">최근 30일</option>
              <option value="custom">날짜 직접 지정 (Custom)...</option>
            </select>
          </div>

          <div>
            <label style={{ display: "block", marginBottom: 6 }}>출력 형식</label>
            <select
              value={selectedFormat}
              onChange={(e) => setSelectedFormat(e.target.value)}
              style={{ width: "100%" }}
            >
              <option value="csv">CSV (스프레드시트/Pandas)</option>
              <option value="json">JSON (웹/API 연동)</option>
            </select>
          </div>
        </div>

        {dateMode === "custom" && (
          <div
            style={{
              marginTop: "var(--space-3)",
              display: "flex",
              flexWrap: "wrap",
              gap: "var(--space-3)",
              padding: "var(--space-2) var(--space-3)",
              background: "var(--bg-secondary)",
              borderRadius: "var(--radius-sm)",
              border: "1px solid var(--border)",
            }}
          >
            <div>
              <label style={{ display: "block", fontSize: "var(--text-xs)", marginBottom: 4 }}>
                시작일 (Start Date)
              </label>
              <input
                type="date"
                value={customStartDate}
                onChange={(e) => setCustomStartDate(e.target.value)}
                style={{ padding: "4px 8px" }}
              />
            </div>
            <div>
              <label style={{ display: "block", fontSize: "var(--text-xs)", marginBottom: 4 }}>
                종료일 (End Date)
              </label>
              <input
                type="date"
                value={customEndDate}
                onChange={(e) => setCustomEndDate(e.target.value)}
                style={{ padding: "4px 8px" }}
              />
            </div>
          </div>
        )}

        {selectedTable === "trades" && (
          <div style={{ marginTop: "var(--space-3)", maxWidth: "240px" }}>
            <label style={{ display: "block", marginBottom: 6 }}>전략 필터 (선택)</label>
            <select
              value={selectedStrategy}
              onChange={(e) => setSelectedStrategy(e.target.value)}
              style={{ width: "100%" }}
            >
              <option value="">모든 전략 (전체)</option>
              <option value="I">전략 I (Confluence Reversion)</option>
              <option value="F">전략 F (Funding Carry)</option>
              <option value="G">전략 G (KORU Short Carry)</option>
              <option value="E">전략 E (Cross Momentum)</option>
            </select>
          </div>
        )}

        <div style={{ marginTop: "var(--space-4)" }}>
          <a
            href={getDownloadUrl()}
            download
            className="btn-success"
          >
            <Download size={15} />
            <span>선택 데이터셋 다운로드 ({selectedFormat.toUpperCase()})</span>
          </a>
        </div>
      </div>

      {/* 4. Terminal & CLI Helper Snippets */}
      <div className="card">
        <div className="card-title-row">
          <div className="card-title">
            <Terminal size={18} color="var(--yellow)" />
            <span>CLI 및 백테스트 명령어 헬퍼</span>
          </div>
        </div>

        <p style={{ fontSize: "var(--text-sm)", color: "var(--text-secondary)", marginBottom: "var(--space-4)" }}>
          서버 터미널에서 직접 대용량 덤프를 뜨거나 로컬에서 백테스트를 실행할 때 사용하는 공식 CLI 스니펫입니다.
        </p>

        <div style={{ marginBottom: "var(--space-4)" }}>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 6 }}>
            <span style={{ fontSize: "var(--text-xs)", fontWeight: 700, color: "var(--text-secondary)" }}>
              1) Beelink 서버 원천 데이터 고속 덤프 (Docker COPY)
            </span>
            <button
              className="btn-secondary"
              onClick={() => handleCopy(dockerCliSnippet, "docker")}
              style={{ fontSize: "var(--text-xs)", padding: "4px 8px" }}
            >
              {copiedCli === "docker" ? <CheckCircle2 size={12} color="var(--green)" /> : <Copy size={12} />}
              <span>{copiedCli === "docker" ? "복사됨!" : "명령어 복사"}</span>
            </button>
          </div>
          <pre className="code-block" style={{ padding: "10px 14px", fontSize: "var(--text-sm)" }}>
            {dockerCliSnippet}
          </pre>
        </div>

        <div>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 6 }}>
            <span style={{ fontSize: "var(--text-xs)", fontWeight: 700, color: "var(--text-secondary)" }}>
              2) 오프라인 퀀트 백테스터 실행 (Python Backtest Engine)
            </span>
            <button
              className="btn-secondary"
              onClick={() => handleCopy(backtestCliSnippet, "backtest")}
              style={{ fontSize: "var(--text-xs)", padding: "4px 8px" }}
            >
              {copiedCli === "backtest" ? <CheckCircle2 size={12} color="var(--green)" /> : <Copy size={12} />}
              <span>{copiedCli === "backtest" ? "복사됨!" : "명령어 복사"}</span>
            </button>
          </div>
          <pre className="code-block" style={{ padding: "10px 14px", fontSize: "var(--text-sm)" }}>
            {backtestCliSnippet}
          </pre>
        </div>
      </div>
    </div>
  );
}
